import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Loader2, User, Crown, Lock } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";

const empty = { name: "", title: "", active: true, working_hours: null };
const DAYS = [["monday", "Pazartesi"], ["tuesday", "Salı"], ["wednesday", "Çarşamba"], ["thursday", "Perşembe"], ["friday", "Cuma"], ["saturday", "Cumartesi"], ["sunday", "Pazar"]];
const defDay = () => ({ open: "09:00", close: "18:00", closed: false, breaks: [] });

export default function Staff() {
  const qc = useQueryClient();
  const { data: staff = [], isLoading } = useQuery({ queryKey: ["staff"], queryFn: () => api.get("/staff").then((r) => r.data) });
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [delId, setDelId] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openNew = () => { setEditing(null); setForm(empty); setDialog(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, title: s.title || "", active: s.active, working_hours: s.working_hours || null }); setDialog(true); };

  const { data: biz } = useQuery({ queryKey: ["business"], queryFn: () => api.get("/business").then((r) => r.data) });
  const isPro = biz?.subscription_state === "active";
  const customHours = form.working_hours !== null && form.working_hours !== undefined;
  const toggleCustom = (on) => {
    if (on) {
      const base = biz?.working_hours || {};
      const wh = {};
      DAYS.forEach(([k]) => { wh[k] = base[k] ? { ...base[k], breaks: base[k].breaks || [] } : defDay(); });
      setForm((f) => ({ ...f, working_hours: wh }));
    } else setForm((f) => ({ ...f, working_hours: null }));
  };
  const setDay = (k, patch) => setForm((f) => ({ ...f, working_hours: { ...f.working_hours, [k]: { ...f.working_hours[k], ...patch } } }));

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/staff/${editing.id}`, form);
      else await api.post("/staff", form);
      qc.invalidateQueries({ queryKey: ["staff"] });
      toast.success(editing ? "Personel güncellendi" : "Personel eklendi");
      setDialog(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };
  const doDelete = async () => {
    try { await api.delete(`/staff/${delId}`); qc.invalidateQueries({ queryKey: ["staff"] }); toast.success("Personel silindi"); }
    catch (err) { toast.error(formatApiError(err)); }
    setDelId(null);
  };

  return (
    <div className="space-y-6" data-testid="staff-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Personel</h1>
          <p className="text-muted-foreground mt-1">Çalışanlarınızı yönetin</p>
        </div>
        <Button onClick={openNew} disabled={!isPro} className="rounded-lg" data-testid="add-staff-btn"><Plus className="w-4 h-4 mr-1" /> Personel Ekle</Button>
      </div>

      {!isPro && (
        <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-accent/40 p-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="staff-pro-lock">
          <div className="w-11 h-11 rounded-xl bg-primary flex items-center justify-center shrink-0">
            <Crown className="w-6 h-6 text-primary-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display font-bold text-lg leading-tight flex items-center gap-2">
              <Lock className="w-4 h-4" /> Personel yönetimi Pro'ya özel
            </p>
            <p className="text-sm text-muted-foreground mt-0.5">
              Birden fazla personel ekleyin, her personele özel çalışma saatleri ve personel bazlı takvim kullanın.
            </p>
          </div>
          <Link to="/panel/abonelik" className="shrink-0">
            <Button className="rounded-full h-11 px-6" data-testid="staff-upgrade-btn">
              <Crown className="w-4 h-4 mr-1.5" /> Pro'ya Geç
            </Button>
          </Link>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : staff.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="no-staff">
          Henüz personel eklemediniz.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {staff.map((s) => (
            <div key={s.id} className="rounded-xl border border-border bg-card p-5" data-testid="staff-card">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-full bg-accent flex items-center justify-center shrink-0">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold truncate">{s.name}</h3>
                  {s.title && <p className="text-sm text-muted-foreground truncate">{s.title}</p>}
                  <span className={cn("inline-block mt-1 text-xs font-semibold px-2 py-0.5 rounded-full",
                    s.active ? "bg-emerald-100 text-emerald-700" : "bg-secondary text-muted-foreground")}>
                    {s.active ? "Aktif" : "Pasif"}
                  </span>
                </div>
              </div>
              <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                <Button size="sm" variant="ghost" className="ml-auto" onClick={() => openEdit(s)} data-testid="edit-staff-btn"><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setDelId(s.id)} data-testid="delete-staff-btn"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent data-testid="staff-dialog">
          <DialogHeader><DialogTitle>{editing ? "Personeli Düzenle" : "Yeni Personel"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Ad Soyad</Label><Input value={form.name} onChange={set("name")} className="h-11 mt-1.5" placeholder="Ahmet Yılmaz" data-testid="staff-name-input" /></div>
            <div><Label>Ünvan (opsiyonel)</Label><Input value={form.title} onChange={set("title")} className="h-11 mt-1.5" placeholder="Kuaför / Berber" data-testid="staff-title-input" /></div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="staff-active-toggle" />
              <span className="text-sm">Aktif</span>
            </div>
            <div className="border-t border-border pt-4">
              <div className="flex items-center gap-2">
                <Switch checked={customHours} onCheckedChange={toggleCustom} data-testid="staff-custom-hours-toggle" />
                <span className="text-sm">Bu personele özel çalışma saatleri</span>
              </div>
              {customHours && (
                <div className="mt-3 space-y-2 max-h-64 overflow-y-auto pr-1" data-testid="staff-hours-editor">
                  {DAYS.map(([k, label]) => (
                    <div key={k} className="flex items-center gap-2 text-sm">
                      <span className="w-24 shrink-0">{label}</span>
                      <Switch checked={!form.working_hours[k]?.closed} onCheckedChange={(v) => setDay(k, { closed: !v })} data-testid={`staff-day-${k}-toggle`} />
                      {form.working_hours[k]?.closed ? (
                        <span className="text-muted-foreground text-xs">Kapalı</span>
                      ) : (
                        <>
                          <Input type="time" value={form.working_hours[k]?.open || "09:00"} onChange={(e) => setDay(k, { open: e.target.value })} className="h-9 w-28" data-testid={`staff-day-${k}-open`} />
                          <Input type="time" value={form.working_hours[k]?.close || "18:00"} onChange={(e) => setDay(k, { close: e.target.value })} className="h-9 w-28" data-testid={`staff-day-${k}-close`} />
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(false)}>İptal</Button>
            <Button onClick={save} disabled={saving || !form.name} data-testid="save-staff-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Personeli sil?</AlertDialogTitle>
            <AlertDialogDescription>Bu işlem geri alınamaz.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground" data-testid="confirm-delete-staff">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
